import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Briefcase, Upload, Send, Paperclip } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { nome: "", email: "", telefone: "", cidade_estado: "", area_interesse: "", mensagem: "" };
const AREAS = [
  "Técnico(a) de Segurança do Trabalho",
  "Engenheiro(a) de Segurança",
  "Bombeiro Civil / Resgatista",
  "Alpinista Industrial",
  "Espaço Confinado / Vigia",
  "Instrutor de NR",
  "Manutenção Mecânica",
  "Administrativo / Comercial",
  "Outros",
];

export const Careers = () => {
  const [form, setForm] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const okTypes = [".pdf", ".doc", ".docx"];
    if (!okTypes.some((ext) => f.name.toLowerCase().endsWith(ext))) {
      toast.error("Formato inválido. Envie PDF, DOC ou DOCX.");
      e.target.value = "";
      setFile(null);
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error(`Arquivo muito grande (${(f.size / 1024 / 1024).toFixed(1)}MB). Máximo 5MB.`);
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(f);
    toast.success(`Currículo anexado: ${f.name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.nome) missing.push("Nome");
    if (!form.email) missing.push("E-mail");
    if (!form.telefone) missing.push("Telefone");
    if (!form.area_interesse) missing.push("Área de interesse");
    if (!file) missing.push("Currículo (anexo)");
    if (missing.length) {
      toast.error(`Preencha: ${missing.join(", ")}`);
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("curriculo", file);
      const res = await axios.post(`${API}/careers`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      if (res.data?.success) {
        toast.success("Currículo enviado! O RH da HSE entrará em contato caso haja aderência à vaga.");
        setForm(INITIAL);
        setFile(null);
        e.target.reset();
      } else {
        toast.error("Envio incompleto. Tente novamente.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Não foi possível enviar. Tente novamente.";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d.msg).join("; ");
      else if (err?.code === "ECONNABORTED") msg = "Tempo esgotado. Tente novamente em alguns segundos.";
      else if (err?.message?.includes("Network")) msg = "Sem conexão com o servidor. Verifique sua internet.";
      toast.error(msg);
      console.error("Careers submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0B1B2D] border border-white/15 focus:border-[#2FA36B] outline-none text-white font-body text-sm px-4 py-3.5 transition-colors placeholder:text-white/30";

  return (
    <section id="carreiras" data-testid="section-carreiras" className="relative py-24 sm:py-32 bg-[#0F2A44]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#2FA36B]" />
              <span className="font-condensed uppercase text-xs tracking-[0.4em] text-[#2FA36B] font-bold">Trabalhe Conosco</span>
            </div>
            <h2 className="font-condensed font-bold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
              Faça parte da <span className="text-[#2FA36B]">HSE</span>.
            </h2>
            <p className="mt-6 text-white/70 font-body text-base leading-relaxed max-w-md">
              Buscamos profissionais técnicos, engajados e comprometidos com segurança, qualidade e serviços críticos. Envie seu currículo e nossa equipe de RH avaliará seu perfil.
            </p>

            <div className="mt-10 flex items-start gap-4 border border-white/10 p-5 bg-[#12283F]">
              <div className="w-11 h-11 bg-[#2FA36B] flex items-center justify-center shrink-0">
                <Briefcase size={20} className="text-white" />
              </div>
              <div>
                <p className="font-condensed uppercase text-[10px] tracking-[0.3em] text-[#2FA36B] font-bold">Recursos Humanos</p>
                <p className="font-body text-white text-sm mt-1">contato@hsedobrasil.com.br</p>
                <p className="font-body text-white/50 text-xs mt-1">Envio automático para o RH após submissão</p>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            noValidate
            data-testid="careers-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7 border border-white/10 bg-[#12283F] p-6 sm:p-10"
            encType="multipart/form-data"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Nome *</label>
                <input data-testid="careers-nome" required className={inputCls} value={form.nome} onChange={handle("nome")} placeholder="Seu nome completo" />
              </div>
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">E-mail *</label>
                <input data-testid="careers-email" required type="email" className={inputCls} value={form.email} onChange={handle("email")} placeholder="voce@exemplo.com" />
              </div>
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Telefone / WhatsApp *</label>
                <input data-testid="careers-telefone" required className={inputCls} value={form.telefone} onChange={handle("telefone")} placeholder="(00) 00000-0000" />
              </div>
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Cidade / Estado</label>
                <input data-testid="careers-cidade" className={inputCls} value={form.cidade_estado} onChange={handle("cidade_estado")} placeholder="São José dos Campos / SP" />
              </div>
              <div className="sm:col-span-2">
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Área de interesse *</label>
                <select data-testid="careers-area" required className={inputCls} value={form.area_interesse} onChange={handle("area_interesse")}>
                  <option value="">Selecione...</option>
                  {AREAS.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Mensagem (opcional)</label>
              <textarea data-testid="careers-mensagem" rows={4} className={inputCls} value={form.mensagem} onChange={handle("mensagem")} placeholder="Fale brevemente sobre sua experiência e disponibilidade" />
            </div>

            <div className="mt-6">
              <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">
                Currículo * <span className="text-white/40 normal-case">(PDF, DOC ou DOCX — máx. 5MB)</span>
              </label>
              <label className="flex items-center gap-3 border-2 border-dashed border-white/20 hover:border-[#2FA36B] cursor-pointer p-5 transition-colors">
                <div className="w-11 h-11 bg-[#2FA36B] flex items-center justify-center shrink-0">
                  <Upload size={18} className="text-white" />
                </div>
                <div className="flex-1">
                  {file ? (
                    <div className="flex items-center gap-2">
                      <Paperclip size={14} className="text-[#2FA36B]" />
                      <span className="font-body text-white text-sm break-all">{file.name}</span>
                      <span className="font-body text-white/40 text-xs">({(file.size / 1024).toFixed(0)} KB)</span>
                    </div>
                  ) : (
                    <span className="font-body text-white/60 text-sm">Clique para anexar seu currículo</span>
                  )}
                </div>
                <input
                  data-testid="careers-file"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={onFile}
                  className="hidden"
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="careers-submit"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 font-condensed uppercase text-sm tracking-[0.2em] font-bold bg-[#2FA36B] hover:bg-[#37B87A] disabled:opacity-60 text-white px-8 py-5 transition-colors"
            >
              {loading ? "Enviando..." : "Enviar candidatura"}
              <Send size={16} />
            </button>

            <p className="mt-5 font-body text-xs text-white/45 leading-relaxed">
              Ao enviar, você concorda com o tratamento de seus dados pessoais pelo RH da HSE, exclusivamente para fins de processo seletivo, conforme nossa Política de Privacidade (LGPD).
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Careers;
